/**
 * LineItemOverrideModal — opens after CatalogPicker selects a SKU.
 * Lets the user set quantity, optionally override the unit price (within
 * their role's policy cap), and pick / type a reason.
 *
 * Props
 *   item           — catalog row from CatalogPicker.onSelect
 *   onConfirm(li)  — { catalogSku, displayName, quantity, listPriceCents,
 *                      unitPriceCents, overrideAmountCents,
 *                      overrideReason }
 *   onCancel()     — close without confirming
 *
 * Live UX
 *   - Override input shows: "Was $4,995.00 → Now $X (−$Y / −Z%)"
 *   - Reason dropdown (enum) appears only when override != 0
 *   - "Other" → reveals free-text input for the custom reason
 *   - Confirm button disabled until reason filled when override != 0
 *   - Server-side validation runs on every typed change via
 *     catalog:check-override; the UI shows the policy reject message
 *     immediately if the override exceeds the user's cap
 *
 * Design ref: docs/product_catalog_design_2026-06-06.md
 */
"use strict";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { AlertCircle, CheckCircle, X } from "lucide-react";

const REASON_LABELS = {
  promo: "Promotional discount",
  vip_comp: "VIP comp",
  bundle: "Bundle deal",
  physician_referral: "Physician referral",
  other: "Other (free text)",
};

function fmtDollars(cents) {
  if (!Number.isFinite(cents)) return "$0.00";
  const sign = cents < 0 ? "-" : "";
  return sign + "$" + (Math.abs(cents) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function LineItemOverrideModal({ item, onConfirm, onCancel }) {
  const listCents = Number(item.list_price_cents) || 0;
  const [quantity, setQuantity] = useState(1);
  const [unitDollars, setUnitDollars] = useState((listCents / 100).toFixed(2));
  const [reasonKey, setReasonKey] = useState("");
  const [reasonOther, setReasonOther] = useState("");
  const [policy, setPolicy] = useState(null);
  const [reasonEnum, setReasonEnum] = useState(["promo", "vip_comp", "bundle", "physician_referral", "other"]);
  const [policyMessage, setPolicyMessage] = useState(null);
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  const overlayRef = useRef(null);

  // Fetch policy once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await window.zignagenixAPI.invoke("catalog:get-policy-for-me");
        if (cancelled) return;
        setPolicy(r && r.policy);
        if (r && Array.isArray(r.reasonEnum)) setReasonEnum(r.reasonEnum);
        setLoadingPolicy(false);
      } catch (e) {
        if (cancelled) return;
        setLoadingPolicy(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Parse the user-entered unit price into cents (integer math).
  const unitCents = useMemo(() => {
    const n = parseFloat(unitDollars);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 100);
  }, [unitDollars]);

  // Computed override
  const overrideCents = unitCents != null ? unitCents - listCents : 0;
  const overridePct = listCents > 0 ? (overrideCents / listCents) * 100 : 0;
  const hasOverride = overrideCents !== 0;

  // Live policy check (debounced via the cents derivation; called once per change).
  useEffect(() => {
    if (!hasOverride || unitCents == null) { setPolicyMessage(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await window.zignagenixAPI.invoke("catalog:check-override", {
          sku: item.sku,
          unitPriceCents: unitCents,
        });
        if (cancelled) return;
        if (r && r.ok === false) setPolicyMessage({ kind: "error", text: r.message });
        else setPolicyMessage({ kind: "ok", text: "Override within policy" });
      } catch (e) {
        if (cancelled) return;
        setPolicyMessage({ kind: "error", text: e && e.message || "Could not check policy" });
      }
    })();
    return () => { cancelled = true; };
  }, [hasOverride, unitCents, item.sku]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onCancel && onCancel(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  function handleOverlayClick(e) {
    if (e.target === overlayRef.current) onCancel && onCancel();
  }

  function buildReasonString() {
    if (!hasOverride) return null;
    if (!reasonKey) return null;
    if (reasonKey === "other") return reasonOther.trim() ? `other:${reasonOther.trim()}` : null;
    return reasonKey;
  }

  const canConfirm = (
    !loadingPolicy &&
    Number.isFinite(quantity) && quantity > 0 &&
    unitCents != null && unitCents >= 0 &&
    (!hasOverride || (buildReasonString() && (!policyMessage || policyMessage.kind === "ok")))
  );

  // When the user is blocked by their policy cap (NOT by a view-only role),
  // they get a "Submit for approval" path instead of a dead end.
  const isCapExceeded = (
    hasOverride &&
    policyMessage && policyMessage.kind === "error" &&
    policy && policy.can_override === true   // sales/manager-tier with caps
  );
  const [approvalState, setApprovalState] = useState({ status: "idle", message: null });

  async function handleSubmitForApproval() {
    if (!isCapExceeded) return;
    const reasonString = buildReasonString();
    if (!reasonString) {
      setApprovalState({ status: "error", message: "Reason required before submitting for approval." });
      return;
    }
    setApprovalState({ status: "submitting", message: null });
    try {
      const r = await window.zignagenixAPI.invoke("catalog:override-request:submit", {
        catalogSku: item.sku,
        requestedUnitPriceCents: unitCents,
        overrideReason: reasonString,
        quantity,
      });
      if (r && r.error) {
        setApprovalState({ status: "error", message: r.message || "Request failed" });
      } else {
        setApprovalState({ status: "submitted", message: `Approval request #${r.id} submitted. Manager will review.` });
      }
    } catch (e) {
      setApprovalState({ status: "error", message: e && e.message || "Submit failed" });
    }
  }

  function handleConfirm() {
    if (!canConfirm) return;
    const reasonString = buildReasonString();
    onConfirm({
      catalogSku: item.sku,
      displayName: item.display_name,
      quantity,
      listPriceCents: listCents,
      unitPriceCents: unitCents,
      overrideAmountCents: overrideCents,
      overrideReason: reasonString,
    });
  }

  // Visual style helpers — human-readable policy summary.
  let policyText;
  if (!policy) {
    policyText = "policy not configured";
  } else if (!policy.can_override) {
    policyText = "view-only (no override)";
  } else {
    const parts = [];
    if (policy.max_discount_pct != null) parts.push(`${policy.max_discount_pct}%`);
    if (policy.max_discount_cents != null) parts.push(`$${(policy.max_discount_cents / 100).toFixed(0)}`);
    policyText = parts.length > 0 ? parts.join(" / ") + " cap" : "unlimited override authority";
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9100, padding: 20,
      }}
    >
      <div style={{
        background: "#F8F9FA", borderRadius: 14, padding: 0,
        width: "100%", maxWidth: 540, maxHeight: "85vh",
        display: "flex", flexDirection: "column", overflow: "hidden",
        boxShadow: "0 18px 48px rgba(0,0,0,0.22)",
      }}>
        {/* Header */}
        <div style={{
          padding: "14px 18px", borderBottom: "1px solid #E9ECEF",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#212529" }}>{item.display_name}</h3>
            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#6C757D" }}>
              <span style={{ fontFamily: "ui-monospace, monospace" }}>{item.sku}</span>
              {" · "} list {fmtDollars(listCents)}
              {item.exosomes_included > 0 ? ` · ${item.exosomes_included} exosomes` : ""}
              {item.hotel_nights > 0 ? ` · ${item.hotel_nights} hotel night${item.hotel_nights > 1 ? "s" : ""}` : ""}
            </p>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close"
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6, borderRadius: 8 }}>
            <X size={18} color="#6C757D" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Quantity */}
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 }}>Quantity</label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
              style={{
                width: 120, padding: "8px 10px", border: "1px solid #DEE2E6",
                borderRadius: 8, fontSize: 14, background: "#fff",
              }}
            />
          </div>

          {/* Unit price */}
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 }}>
              Unit price (per item) — applied per unit
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14, color: "#6C757D" }}>$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={unitDollars}
                onChange={(e) => setUnitDollars(e.target.value)}
                style={{
                  flex: 1, padding: "8px 10px", border: "1px solid #DEE2E6",
                  borderRadius: 8, fontSize: 14, background: "#fff",
                }}
              />
              <button
                type="button"
                onClick={() => setUnitDollars((listCents / 100).toFixed(2))}
                title="Reset to list price"
                style={{
                  padding: "8px 10px", border: "1px solid #DEE2E6", borderRadius: 8,
                  background: "#fff", fontSize: 12, color: "#495057", cursor: "pointer",
                }}
              >Reset</button>
            </div>
            {/* Live diff */}
            <div style={{ marginTop: 6, fontSize: 12 }}>
              {!hasOverride ? (
                <span style={{ color: "#6C757D" }}>No override — list price</span>
              ) : (
                <span style={{ color: overrideCents < 0 ? "#0F766E" : "#B45309", fontWeight: 600 }}>
                  Was {fmtDollars(listCents)} → Now {fmtDollars(unitCents)}
                  {"  "}({overrideCents < 0 ? "−" : "+"}{fmtDollars(Math.abs(overrideCents))} / {overridePct >= 0 ? "+" : ""}{overridePct.toFixed(1)}%)
                </span>
              )}
              <span style={{ marginLeft: 10, color: "#ADB5BD" }}>· Your cap: {policyText}</span>
            </div>
          </div>

          {/* Reason — required when override != 0 */}
          {hasOverride && (
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 }}>
                Reason for override <span style={{ color: "#B91C1C" }}>*</span>
              </label>
              <select
                value={reasonKey}
                onChange={(e) => setReasonKey(e.target.value)}
                style={{
                  width: "100%", padding: "8px 10px", border: "1px solid #DEE2E6",
                  borderRadius: 8, fontSize: 14, background: "#fff",
                }}
              >
                <option value="">— pick a reason —</option>
                {reasonEnum.map(k => (
                  <option key={k} value={k}>{REASON_LABELS[k] || k}</option>
                ))}
              </select>
              {reasonKey === "other" && (
                <input
                  type="text"
                  value={reasonOther}
                  onChange={(e) => setReasonOther(e.target.value)}
                  placeholder="Describe the reason for this override"
                  style={{
                    marginTop: 6, width: "100%", padding: "8px 10px",
                    border: "1px solid #DEE2E6", borderRadius: 8, fontSize: 14, background: "#fff",
                  }}
                />
              )}
            </div>
          )}

          {/* Policy status */}
          {policyMessage && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 12px", borderRadius: 8, fontSize: 12,
              background: policyMessage.kind === "error" ? "#FEE2E2" : "#DCFCE7",
              color: policyMessage.kind === "error" ? "#7F1D1D" : "#14532D",
            }}>
              {policyMessage.kind === "error"
                ? <AlertCircle size={14} />
                : <CheckCircle size={14} />}
              <span>{policyMessage.text}</span>
            </div>
          )}

          {/* Line total */}
          <div style={{
            marginTop: 6, padding: "10px 12px", background: "#fff",
            border: "1px solid #DEE2E6", borderRadius: 8,
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ fontSize: 12, color: "#6C757D" }}>Line total ({quantity} × unit)</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: "#2E8B98" }}>
              {fmtDollars((unitCents || 0) * quantity)}
            </span>
          </div>
        </div>

        {/* Approval status message */}
        {approvalState.message && (
          <div style={{
            margin: "0 18px 8px",
            padding: "8px 12px", borderRadius: 8, fontSize: 12,
            background: approvalState.status === "submitted" ? "#DCFCE7" : "#FEE2E2",
            color: approvalState.status === "submitted" ? "#14532D" : "#7F1D1D",
          }}>{approvalState.message}</div>
        )}

        {/* Footer */}
        <div style={{
          padding: "12px 18px", borderTop: "1px solid #E9ECEF",
          display: "flex", justifyContent: "flex-end", gap: 8, background: "#F1F3F5",
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "8px 14px", border: "1px solid #DEE2E6", borderRadius: 8,
              background: "#fff", fontSize: 13, color: "#495057", cursor: "pointer",
            }}
          >Cancel</button>
          {isCapExceeded && approvalState.status !== "submitted" && (
            <button
              type="button"
              onClick={handleSubmitForApproval}
              disabled={approvalState.status === "submitting" || !buildReasonString()}
              style={{
                padding: "8px 16px", border: "1px solid #B45309", borderRadius: 8,
                background: "#FFFBEB", color: "#7A4A00", fontSize: 13, fontWeight: 600,
                cursor: approvalState.status === "submitting" ? "not-allowed" : "pointer",
              }}
              title="Sends the override to a manager for approval"
            >
              {approvalState.status === "submitting" ? "Submitting…" : "Submit for approval"}
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            style={{
              padding: "8px 16px", border: "none", borderRadius: 8,
              background: canConfirm ? "#2E8B98" : "#ADB5BD",
              color: "#fff", fontSize: 13, fontWeight: 600,
              cursor: canConfirm ? "pointer" : "not-allowed",
            }}
          >Add line item</button>
        </div>
      </div>
    </div>
  );
}
